import { GraphQLResult } from "@aws-amplify/api-graphql";
import { ExportCsv } from "@material-table/exporters";
import { Box, debounce, Grid } from "@mui/material";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import {
  SearchableIDMSLogRecordV1FilterInput,
  SearchIDMSLogRecordV1sQuery,
} from "../../API";
import EventService from "../../EventService";
import DetailField from "../Detail/DetailField";
import GenericTable from "../HealthCareReview/GenericTable";

export default function LogPage() {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [logRecords, setLogRecords] = useState<any>([]);
  const [startDate, setStartDate] = useState<string | null | undefined>("");
  const [endDate, setEndDate] = useState<string | null | undefined>("");

  const disableDates = (date: any) => {
    if (startDate && endDate) {
      const dayDifference =
        (date.getTime() - new Date(startDate).getTime()) / (1000 * 3600 * 24);
      return date <= new Date(startDate) || dayDifference > 2;
    }
    return false;
  };

  const handleStartDateChange = (date: string | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: string | null) => {
    setEndDate(date);
  };

  const [appliedFilter, setAppliedFilter] =
    useState<SearchableIDMSLogRecordV1FilterInput>();
  const handleFilterUpdate = (
    filter?: SearchableIDMSLogRecordV1FilterInput
  ) => {
    setAppliedFilter(filter);
  };

  const loadLogRecords = async (
    pStartDate: string,
    pEndDate: string,
    pAppliedFilter?: SearchableIDMSLogRecordV1FilterInput
  ) => {
    if (!pStartDate || !pEndDate) {
      setLogRecords([]);
      return;
    }
    setIsLoading(true);
    try {
      const logRecordArray = [];

      const filter = {
        and: [
          {
            createdAt: {
              range: [
                pStartDate,
                moment(pEndDate).add(1, "days").format("YYYY-MM-DD"),
              ],
            },
          },
        ],
      } as unknown as SearchableIDMSLogRecordV1FilterInput;

      if (pAppliedFilter) {
        filter.and?.push(pAppliedFilter);
      }

      let nextToken;

      do {
        const searchLogQuery = (await EventService.searchIdmsLogRecordV1s(
          undefined,
          filter,
          undefined,
          nextToken
        )) as GraphQLResult<SearchIDMSLogRecordV1sQuery>;
        nextToken = searchLogQuery.data?.searchIDMSLogRecordV1s?.nextToken;
        if (searchLogQuery.data?.searchIDMSLogRecordV1s?.items) {
          logRecordArray.push(
            ...searchLogQuery.data?.searchIDMSLogRecordV1s?.items
          );
        }
      } while (nextToken != null);

      setLogRecords(logRecordArray);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      debouncedLoadVaccineRoundRecords(startDate, endDate, appliedFilter);
    }
  }, [startDate, endDate, appliedFilter]);
  const debouncedLoadVaccineRoundRecords = useCallback(
    debounce(
      (
        pStartDate: string,
        pEndDate: string,
        pAppliedFilter?: SearchableIDMSLogRecordV1FilterInput
      ) => {
        loadLogRecords(pStartDate, pEndDate, pAppliedFilter);
      },
      500
    ),
    []
  );

  return (
    <React.Fragment>
      <Box mt={2} mb={2}>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <DetailField
              required
              type="date"
              label="Start Date"
              value={startDate || undefined}
              onChange={handleStartDateChange}
            />
          </Grid>
          <Grid item xs={3}>
            <DetailField
              required
              type="date"
              label="End Date"
              value={endDate || undefined}
              onChange={handleEndDateChange}
              shouldDisableDate={disableDates}
            />
          </Grid>
        </Grid>
      </Box>

      <GenericTable
        title="Log Record"
        columns={[
          {
            title: "Updated By",
            field: "updatedBy",
            width: 100,
          },
          {
            title: "Created At",
            field: "createdAt",
            type: "datetime",
            width: 100,
          },
          {
            title: "Id",
            field: "id",
            defaultSort: "asc",
            width: 100,
          },
          {
            title: "Level",
            field: "level",
            width: 100,
          },
          {
            title: "Action",
            field: "action",
            width: 100,
          },
          {
            title: "ObjectId",
            field: "objectId",
            width: 100,
          },
          {
            title: "Old Message",
            field: "oldMessage",
            width: 100,
          },
          {
            title: "New Message",
            field: "message",
            width: 100,
          },
        ]}
        data={logRecords}
        onFilter={handleFilterUpdate}
        isLoading={isLoading}
        grouping={true}
        optionOverrides={{
          exportAllData: true,
          exportMenu: [
            {
              label: "Export CSV",
              exportFunc: (cols, datas) => ExportCsv(cols, datas, "log-record"),
            },
          ],
          search: true,
        }}
      />
    </React.Fragment>
  );
}
